import type { GetBrandsListResponseType } from "@nlc/shared-types";

import { useQuery } from "@tanstack/react-query";

import { brandsKeys } from "@/hooks/queryKeys";
import { useUser } from "@/hooks/useUser";
import apiClient from "@/lib/api/client";

export function useGetBrandsList({
  direction,
  enabled = true,
  globalMode = false,
  itemsPerPage,
  page,
  projectId: projectIdOverride,
  search,
  sortBy,
}: {
  direction: "asc" | "desc";
  enabled?: boolean;
  globalMode?: boolean;
  itemsPerPage: number;
  page: number;
  projectId?: number;
  search?: string;
  sortBy: string;
}) {
  const { data: user } = useUser();
  const projectId = projectIdOverride ?? user?.currentProjectId;
  const isSuperAdmin = user?.user?.roleName === "super-admin";

  const shouldUseGlobalMode =
    globalMode &&
    user?.user?.roleName &&
    ["team-leader"].includes(user.user.roleName);

  const searchParams = {
    direction,
    globalMode: shouldUseGlobalMode || undefined,
    isSuperAdmin: isSuperAdmin || undefined,
    itemsPerPage,
    page,
    projectId: projectId || undefined,
    search,
    sortBy,
  };

  const shouldEnableQuery =
    !!projectId ||
    isSuperAdmin ||
    (user?.user?.roleName &&
      [
        "admin",
        "customer-success",
        "designer",
        "owner",
        "team-leader",
      ].includes(user.user.roleName));

  return useQuery<GetBrandsListResponseType>({
    enabled: Boolean(shouldEnableQuery) && enabled,
    queryFn: async () => {
      const params = new URLSearchParams({
        direction: direction ?? "desc",
        itemsPerPage: itemsPerPage.toString() ?? 10,
        page: page.toString() ?? 1,
        sortBy: sortBy ?? "createdAt",
        ...(search && { search }),
        ...(shouldUseGlobalMode && { globalMode: "true" }),
        ...(projectId && { projectId: projectId.toString() }),
      });

      return await apiClient.url(`/brands?${params.toString()}`).get().json();
    },
    queryKey: brandsKeys.brandsList(searchParams),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
}
